import React, { useEffect, useMemo, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, ChevronRight, Search, Truck, Star } from 'lucide-react';
import { SERVICE_CITIES, SERVICE_PRODUCT_SLUGS, getServiceProduct, type ServiceCity } from '../data/cityMaterialContent';
import { getRegionMeta } from '../utils/region';
// Sitemap-style index page that links to every generated City × Material
// landing page. The layout splits into a FEATURED row of 3 flagship cities
// (one per region — Pierce home, Seattle flagship, Eastside flagship)
// followed by a compact two-column county-grouped list of the rest. The
// rhythm change keeps the page from feeling like a wall of identical cards.
// Display order of the featured cities at the top of /areas. The filter
// below preserves THIS order (not the order cities appear in
// SERVICE_CITIES) so we can lead with the biggest-market cities.
const FEATURED_SLUGS = ['seattle', 'bellevue', 'tacoma'];
// Subtle zone-driven accent so customers can scan delivery zones across
// the page at a glance. Stays muted — never competes with brand-orange.
// Three delivery tiers: Zone 1 inner (+$80), Zone 2 middle (+$120),
// Zone 3 outer (+$175). Curated city.zone values (legacy 1–5) still map
// onto the correct tier's accent + fee: old 1/2 → inner, old 3 → middle,
// old 4/5 → outer.
const ZONE_ACCENT: Record<number, string> = {
  1: 'border-l-[#04A05C]',
  2: 'border-l-[#04A05C]',
  3: 'border-l-amber-400',
  4: 'border-l-rose-400',
  5: 'border-l-rose-400'
};
const ZONE_FEE: Record<number, string> = {
  1: '+$80',
  2: '+$80',
  3: '+$120',
  4: '+$175',
  5: '+$175'
};
// Legacy curated zone (1–5) → the actual tier number shown to customers.
// Without this the UI printed "Zone 4"/"Zone 5" for a business that only has
// three zones, and the legend below listed Z1/Z2/Z3 straight off the legacy
// map — which rendered two identical +$80 entries and never showed +$175.
const ZONE_TIER: Record<number, 1 | 2 | 3> = {
  1: 1,
  2: 1,
  3: 2,
  4: 3,
  5: 3
};
const ZONE_TIERS: {
  tier: 1 | 2 | 3;
  fee: string;
  dot: string;
}[] = [{
  tier: 1,
  fee: '+$80',
  dot: 'bg-[#04A05C]'
}, {
  tier: 2,
  fee: '+$120',
  dot: 'bg-amber-400'
}, {
  tier: 3,
  fee: '+$175',
  dot: 'bg-rose-400'
}];
export function ServiceAreas() {
  const [query, setQuery] = useState('');
  const regionMeta = getRegionMeta();
  const regionSlugs = regionMeta.cities.map(c => c.toLowerCase().replace(/\s+/g, '-'));
  const FEATURED_SLUGS = Array.from(new Set([...regionSlugs, 'seattle', 'bellevue', 'tacoma'])).slice(0, 3);
  useEffect(() => {
    const orig = document.title;
    document.title = 'Service Areas — 206Gravel Delivery to Tacoma, Seattle, Bellevue, Puyallup & more';
    return () => {
      document.title = orig;
    };
  }, []);
  const products = useMemo(() => SERVICE_PRODUCT_SLUGS.map(s => getServiceProduct(s)).filter(Boolean) as {
    product: any;
    category: any;
  }[], []);
  const matches = (city: ServiceCity) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return city.name.toLowerCase().includes(q) || city.county.toLowerCase().includes(q) || city.neighborhoods.some(n => n.toLowerCase().includes(q));
  };
  // Map over FEATURED_SLUGS (not filter on SERVICE_CITIES) so the
  // display order respects the curated lead-with list above.
  const featured = FEATURED_SLUGS.map(slug => SERVICE_CITIES.find(c => c.slug === slug)).filter(Boolean) as typeof SERVICE_CITIES;
  const featuredVisible = featured.filter(matches);
  // Non-featured grouped by county for the secondary section.
  const remainingByCounty = useMemo(() => {
    const remaining = SERVICE_CITIES.filter(c => !FEATURED_SLUGS.includes(c.slug) && matches(c));
    return {
      King: remaining.filter(c => c.county === 'King'),
      Pierce: remaining.filter(c => c.county === 'Pierce')
    };
  }, [query]);
  const totalMatches = featuredVisible.length + remainingByCounty.King.length + remainingByCounty.Pierce.length;
  return <main className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-3297" data-mp="3r86lq2b6qsxn">
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-3298" data-mp="3r8q1bcdd558i">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400" data-id="element-3299" data-mp="3v88e1kv16a09">
          <li data-id="element-3300" data-mp="44a16jmdgfuf">
            <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-3301" data-mp="44s3nxzmnqas">
              Home
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-3302" data-mp="e1oalz9vo4d8" />
          <li className="text-stone-900 dark:text-white font-semibold" aria-current="page" data-id="element-3303" data-mp="e0d8e3w2z4o8">
            Service Areas
          </li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-8" data-id="element-3304" data-mp="mu846lbbbir7">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-3305" data-mp="rtbae9313v0r">
          <div className="inline-flex items-center gap-2 mb-3" data-id="element-3306" data-mp="wq1s10uheyoa">
            <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-3307" data-mp="16wvz1rzit7ud" />
            <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-3308" data-mp="11x9nro3961o7">
              Puget Sound · King &amp; Pierce County
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 dark:text-white leading-[1.05]" data-id="element-3309" data-mp="swbg8vtlvc0v">
            Where we deliver
          </h1>
          <p className="mt-4 text-stone-600 dark:text-stone-300 max-w-3xl" data-id="element-3310" data-mp="1pof3825a6rxz">
            We deliver gravel, topsoil, bark, and drain rock across South King
            and Pierce County. Tap a city to see pricing, materials, and
            delivery details specific to your neighborhood.
          </p>
        </motion.div>

        {/* Search + zone legend */}
        <div className="mt-6 grid md:grid-cols-[1fr,auto] gap-4 items-center" data-id="element-3311" data-mp="28emy5wytuc33">
          <div className="relative" data-id="element-3312" data-mp="23lan6pn2m3st">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-3313" data-mp="23k8yhid4tism" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search a city or neighborhood…" className="w-full pl-10 pr-4 py-3 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 text-sm text-stone-900 dark:text-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-orange" data-id="element-3314" data-mp="1qwe236lmctp" />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[11px] font-bold text-stone-600 dark:text-stone-300" data-id="element-3315" data-mp="3f24wqcpkrvrc">
            <span className="uppercase tracking-wider text-stone-400 dark:text-stone-500" data-id="element-3316" data-mp="3k0pxqecnijgu">
              Zone fees
            </span>
            {ZONE_TIERS.map(t => <span key={t.tier} className="inline-flex items-center gap-1.5" data-id="element-3317" data-mp="3a5o4b6bcev8g">
                <span className={`block w-2 h-2 rounded-full ${t.dot}`} data-id="element-3318" data-mp="13dng6hre1asq" />
                Z{t.tier} {t.fee}
              </span>)}
          </div>
        </div>
      </section>

      {/* Featured cities */}
      {featuredVisible.length > 0 && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10" data-id="element-3319" data-mp="2svu102y3yg6t">
          <div className="flex items-center gap-2 mb-4" data-id="element-3320" data-mp="2nwza0dpzllbm">
            <Star className="w-4 h-4 text-brand-orange" data-id="element-3321" data-mp="2nud22cz6p3sv" />
            <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-brand-orange" data-id="element-3322" data-mp="32lvootmjtwl7">
              Featured cities
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4" data-id="element-3323" data-mp="3bligqote1q1l">
            {featuredVisible.map((city, idx) => <motion.div key={city.slug} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.05 * idx
        }} id={city.slug} className={`scroll-mt-24 p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 border-l-4 ${ZONE_ACCENT[city.zone]}`} data-id="element-3324" data-mp="37k70vb0bgj6l" data-mpi={idx}>
                <div className="flex items-start justify-between gap-3 mb-4" data-id="element-3325" data-mp="1m73x2ix88tcy">
                  <div data-id="element-3326" data-mp="1m96x7vs3g2ve">
                    <h3 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-3327" data-mp="10e80bpc92wg1">
                      {city.name}
                    </h3>
                    <p className="mt-1 text-[11px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider" data-id="element-3328" data-mp="1a3hsf573gf45">
                      {city.county} County · Zone {ZONE_TIER[city.zone]}
                    </p>
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-stone-100 dark:bg-white/[0.06] text-[11px] font-bold text-stone-700 dark:text-stone-200 shrink-0" data-id="element-3329" data-mp="hfvofvtyf8gr">
                    {ZONE_FEE[city.zone]}
                  </span>
                </div>

                <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed mb-4" data-id="element-3330" data-mp="rfcsk8tk5eam">
                  {city.blurb}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-5" data-id="element-3331" data-mp="22r3wsfo5bns2">
                  {city.neighborhoods.slice(0, 4).map(n => <span key={n} className="px-2 py-0.5 rounded-full bg-stone-100 dark:bg-white/[0.06] text-xs font-medium text-stone-600 dark:text-stone-400" data-id="element-3332" data-mp="27px9w6zitcrp">
                      {n}
                    </span>)}
                  {city.neighborhoods.length > 4 && <span className="px-2 py-0.5 text-[10px] font-medium text-stone-400" data-id="element-3333" data-mp="3ijhiw0fto8jb">
                      +{city.neighborhoods.length - 4} more
                    </span>}
                </div>

                <div className="grid grid-cols-2 gap-1.5 mb-4" data-id="element-3334" data-mp="3dijyrib954f6">
                  {products.map(({
              product
            }) => <Link key={product.slug} to={`/gravel-delivery/${city.slug}/${product.slug}`} className="group px-3 py-2 rounded-lg bg-stone-50 dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-3335" data-mp="34j6rmwalorxm">
                      <div className="text-xs font-bold text-stone-900 dark:text-white truncate group-hover:text-brand-orange transition-colors" data-id="element-3336" data-mp="2unmyghnf87ck">
                        {product.name}
                      </div>
                      <div className="text-[10px] text-stone-500 dark:text-stone-400" data-id="element-3337" data-mp="sscg9nlk424j">
                        ${product.pricePerYard}/yd
                      </div>
                    </Link>)}
                </div>

                <Link to={`/delivery-zones?address=${encodeURIComponent(city.geocodeHint)}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange hover:underline" data-id="element-3338" data-mp="3tg1jj0w299w5">
                  See {city.name} on the zone map
                  <ArrowRight className="w-3 h-3" data-id="element-3339" data-mp="31px2nc7if4zo" />
                </Link>
              </motion.div>)}
          </div>
        </section>}

      {/* More service areas — county-grouped */}
      {(remainingByCounty.Pierce.length > 0 || remainingByCounty.King.length > 0) && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-3340" data-mp="35qz0bols38zj">
          <div className="flex items-center gap-2 mb-4" data-id="element-3341" data-mp="35ow1cf2ne5io">
            <Truck className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-3342" data-mp="2hztmtg3b8sse" />
            <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400" data-id="element-3343" data-mp="2e0chcmnvvvn8">
              More service areas
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-3344" data-mp="28mcghhtvi87w">
            {(['King', 'Pierce'] as const).map(county => {
          const list = remainingByCounty[county];
          if (list.length === 0) return null;
          return <div key={county} data-id="element-3345" data-mp="1yvzx3sxsol3c">
                  {/* County name links to the county landing page, which
                      aggregates every city and material for that county. */}
                  <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-3" data-id="element-3346" data-mp="2rkx4v24dqzhu">
                    <Link to={`/gravel-delivery/${county.toLowerCase()}-county`} className="hover:text-brand-orange transition-colors" data-id="element-3347" data-mp="2co6c3bp7om17">
                      {county} County
                    </Link>
                    <span className="ml-2 text-xs font-normal text-stone-400" data-id="element-3348" data-mp="11910tjb0lf5z">
                      {list.length} cit{list.length === 1 ? 'y' : 'ies'}
                    </span>
                  </h3>
                  <div className="space-y-3" data-id="element-3349" data-mp="38v10w0u1nzsv">
                    {list.map(city => <div key={city.slug} id={city.slug} className={`scroll-mt-24 p-4 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 border-l-4 ${ZONE_ACCENT[city.zone]}`} data-id="element-3350" data-mp="38wlthkwst8o3">
                        <div className="flex items-center justify-between gap-3 mb-2" data-id="element-3351" data-mp="2u5mqqqin9ecf">
                          <div data-id="element-3352" data-mp="2u3sgc65014ml">
                            <p className="font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-3353" data-mp="2p71qir3e6unz">
                              {city.name}
                            </p>
                            <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider" data-id="element-3354" data-mp="2g5u5hapvzqbu">
                              Zone {ZONE_TIER[city.zone]} ·{' '}
                              {city.neighborhoods.length}+ neighborhoods
                            </p>
                          </div>
                          <span className="px-2 py-0.5 rounded-full bg-stone-100 dark:bg-white/[0.06] text-[10px] font-bold text-stone-700 dark:text-stone-200 shrink-0" data-id="element-3355" data-mp="9f4fo6glylfu">
                            {ZONE_FEE[city.zone]}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-id="element-3356" data-mp="qozuj1349u9v">
                          {products.map(({
                    product
                  }) => <Link key={product.slug} to={`/gravel-delivery/${city.slug}/${product.slug}`} className="inline-flex items-center px-2.5 py-1 rounded-full bg-stone-100 dark:bg-white/[0.06] text-xs font-bold text-stone-700 dark:text-stone-200 hover:bg-brand-orange/10 hover:text-brand-orange transition-colors" data-id="element-3357" data-mp="vmhyed2a1jb0">
                              {product.name}
                            </Link>)}
                        </div>
                      </div>)}
                  </div>
                </div>;
        })}
          </div>
        </section>}

      {/* Empty state */}
      {totalMatches === 0 && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-3358" data-mp="1obfc02vjmrxf">
          <div className="p-10 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 text-center" data-id="element-3359" data-mp="1lxa9m9t2mq0i">
            <p className="text-sm text-stone-700 dark:text-stone-300" data-id="element-3360" data-mp="1hxjj877gibwi">
              No service areas match "{query}". Try a different city or
              neighborhood.
            </p>
            <button type="button" onClick={() => setQuery('')} className="mt-3 text-xs font-bold text-brand-orange hover:underline" data-id="element-3361" data-mp="1qvv7w3orizmb">
              Clear search
            </button>
          </div>
        </section>}

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-3362" data-mp="20szxkdhthd7v">
        <div className="p-6 rounded-2xl bg-stone-900 dark:bg-black border border-stone-800 text-center" data-id="element-3363" data-mp="2elzd2fhln7ff">
          <h2 className="font-heading text-xl font-bold text-white" data-id="element-3364" data-mp="29nxudvo35a4h">
            Not seeing your city?
          </h2>
          <p className="mt-2 text-sm text-stone-300" data-id="element-3365" data-mp="2oikl79ox0ynh">
            We deliver well beyond these cities. Check the zone map or call us
            for a custom quote.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3" data-id="element-3366" data-mp="337vmeujdmx46">
            <Link to="/delivery-zones" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors" data-id="element-3367" data-mp="377nz9jfszpfc">
              View Zone Map
              <ArrowRight className="w-3.5 h-3.5" data-id="element-3368" data-mp="2y6g43a8l34ph" />
            </Link>
            <a href="tel:206-619-3742" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-sm transition-colors" data-id="element-3369" data-mp="2jibpoigkdfci">
              (206) 619-3742
            </a>
          </div>
        </div>
      </section>
    </main>;
}